import { useEffect, useRef, useState } from 'react';
import { catalogsAPI, categoriesAPI, getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { CatalogPosRuleEditor } from '@/components/catalogs/CatalogPosRuleEditor';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { 
  Plus, Laptop, Monitor, Smartphone, Gamepad2, Printer, Wrench, 
  Banknote, FolderTree, Search, Edit, Trash2, ChevronRight,
  Package, Settings, Receipt, CreditCard, ArrowLeftRight,
  AlertTriangle, RotateCcw, Eye, EyeOff, Info, ShieldAlert, X
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { useLanguage } from '@/i18n';
import { resolveProductSchemaKey } from '@/lib/productPosDescription';

const MODAL_INPUT_CLASS = 'h-11 rounded-xl border-slate-300 bg-slate-50';
const MODAL_TEXTAREA_CLASS = 'rounded-xl border-slate-300 bg-slate-50 min-h-[96px]';
const MODAL_SELECT_CLASS = 'h-11 rounded-xl border-slate-300 bg-slate-50';
const MODAL_SECTION_CLASS = 'space-y-2 rounded-2xl border border-slate-200 bg-slate-50/70 p-4';
const CATEGORY_PRIMARY_ATTRIBUTE_DEFAULTS = [
  { key: 'color', label: 'Cor', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
  { key: 'power', label: 'Potência', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
  { key: 'warranty', label: 'Garantia', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
  { key: 'connector', label: 'Conector', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
];
const VARIANT_POLICY_OPTIONS = [
  { value: 'attribute_only', label: 'Só atributo' },
  { value: 'variant_optional', label: 'Variante opcional' },
  { value: 'variant_required', label: 'Variante obrigatória' },
];

export default function CatalogsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('product-categories');
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Data states
  const [productCategories, setProductCategories] = useState([]);
  const [productSubcategories, setProductSubcategories] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [repairServiceTypes, setRepairServiceTypes] = useState([]);
  const [cashOperationTypes, setCashOperationTypes] = useState([]);
  const [cashBoxTypes, setCashBoxTypes] = useState([]);
  const [schemaCatalog, setSchemaCatalog] = useState({});
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [dialogType, setDialogType] = useState(''); // which catalog type
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [seedingCashOps, setSeedingCashOps] = useState(false);
  const loadAllCatalogsRef = useRef(null);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadAllCatalogsRef.current?.();
  }, [showDeleted]);

  const loadAllCatalogs = async () => {
    const failedRequests = [];
    setLoading(true);
    try {
      resetFetchTelemetry();
      const [cats, subcats, services, expenses, repairs, cashOps, boxTypes, schemas] = await Promise.all([
        safeRequest(() => catalogsAPI.listProductCategories(true, showDeleted), { data: [] }, { onError: () => failedRequests.push('product-categories'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.product_categories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listProductSubcategories(null, true, showDeleted), { data: [] }, { onError: () => failedRequests.push('product-subcategories'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.product_subcategories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listServiceCatalog(null, true), { data: [] }, { onError: () => failedRequests.push('service-catalog'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.service_catalog', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listExpenseCategories(true, true, showDeleted), { data: [] }, { onError: () => failedRequests.push('expense-categories'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.expense_categories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listRepairServiceTypes(null, true), { data: [] }, { onError: () => failedRequests.push('repair-service-types'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.repair_service_types', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listCashOperationTypes(null, true), { data: [] }, { onError: () => failedRequests.push('cash-operation-types'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.cash_operation_types', onEvent: captureFetchTelemetry } }),
        catalogsAPI.listCashBoxTypes
          ? safeRequest(() => catalogsAPI.listCashBoxTypes(), { data: [] }, { onError: () => failedRequests.push('cash-box-types'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'catalogs.cash_box_types', onEvent: captureFetchTelemetry } })
          : { data: [] },
        safeRequest(() => categoriesAPI.getAllSchemas(), { data: {} }, { onError: () => failedRequests.push('category-schemas'), retries: 1, required: true, telemetry: { module: 'catalogs', endpoint: 'categories.all_schemas', onEvent: captureFetchTelemetry } }),
      ]);
      setProductCategories(cats.data);
      setProductSubcategories(subcats.data);
      setServiceCatalog(services.data);
      setExpenseCategories(expenses.data);
      setRepairServiceTypes(repairs.data);
      setCashOperationTypes(cashOps.data);
      setCashBoxTypes(boxTypes.data || boxTypes || []);
      setSchemaCatalog(schemas.data || {});
      if (failedRequests.length) {
        toast.info('Alguns catálogos vieram vazios ou indisponíveis, mas a página foi aberta normalmente.');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar catálogos'));
    } finally {
      setLoading(false);
    }
  };

  loadAllCatalogsRef.current = loadAllCatalogs;

  const handleSeedCashOperationTypes = async () => {
    try {
      setSeedingCashOps(true);
      await catalogsAPI.seedCashOperationTypes();
      toast.success('Tipos base de operações TRF repostos com sucesso');
      await loadAllCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao repor operações TRF'));
    } finally {
      setSeedingCashOps(false);
    }
  };

  // Dialog handlers
  const openCreateDialog = (type) => {
    setDialogType(type);
    setDialogMode('create');
    setSelectedItem(null);
    setFormData(type === 'product-category' ? buildProductCategoryDraft(getInitialFormData(type)) : getInitialFormData(type));
    setDialogOpen(true);
  };

  const openEditDialog = (type, item) => {
    setDialogType(type);
    setDialogMode('edit');
    setSelectedItem(item);
    setFormData(type === 'product-category' ? buildProductCategoryDraft({ ...item }) : { ...item });
    setDialogOpen(true);
  };

  const getInitialFormData = (type) => {
    switch (type) {
      case 'product-category':
        return { name: '', base_prefix: '', is_trackable_default: true, description: '', pos_description_enabled: false, pos_description_fields: [], schema_key: '', schema_fields: [], primary_attribute_rules: CATEGORY_PRIMARY_ATTRIBUTE_DEFAULTS };
      case 'product-subcategory':
        return { category_id: '', name: '', prefix: '', is_trackable_override: null, description: '', pos_description_enabled: false, pos_description_fields: [] };
      case 'service-catalog':
        return { name: '', service_group: 'SP', unit_price: '', unit: 'unidade', description: '' };
      case 'expense-category':
        return { name: '', parent_id: null, description: '' };
      case 'repair-service-type':
        return { name: '', category: 'laptop', default_price: '', description: '' };
      case 'cash-operation-type':
        return { name: '', direction: 'transfer', from_account_type: '', to_account_type: '', description: '' };
      case 'cash-box-type':
        return { name: '', location: '', description: '', is_main: false };
      default:
        return {};
    }
  };

  const getSchemaBlueprintForCategory = (basePrefix, explicitSchemaKey = '') => {
    const normalizedPrefix = String(basePrefix || '').toUpperCase();
    const resolvedKey = explicitSchemaKey || resolveProductSchemaKey(normalizedPrefix);
    return schemaCatalog[resolvedKey] || schemaCatalog[normalizedPrefix.toLowerCase()] || null;
  };

  const normalizePrimaryAttributeRules = (rules) => CATEGORY_PRIMARY_ATTRIBUTE_DEFAULTS.map((fallbackRule) => {
    const matched = Array.isArray(rules) ? rules.find((rule) => rule.key === fallbackRule.key) : null;
    return {
      ...fallbackRule,
      ...(matched || {}),
    };
  });

  const normalizeSchemaFields = (rawFields, blueprintFields) => {
    const fallbackMap = Object.fromEntries((blueprintFields || []).map((field) => [field.id, field]));
    const source = Array.isArray(rawFields) && rawFields.length ? rawFields : (blueprintFields || []);
    return source.map((field) => ({
      ...(fallbackMap[field.id] || {}),
      ...field,
      active: field.active !== false,
      required: Boolean(field.required),
    }));
  };

  const buildProductCategoryDraft = (payload) => {
    const blueprint = getSchemaBlueprintForCategory(payload.base_prefix, payload.schema_key);
    return {
      ...payload,
      schema_key: payload.schema_key || blueprint?.category_key || resolveProductSchemaKey(payload.base_prefix || '') || '',
      primary_attribute_rules: normalizePrimaryAttributeRules(payload.primary_attribute_rules || blueprint?.primary_attribute_fields),
      schema_fields: normalizeSchemaFields(payload.schema_fields, blueprint?.schema_fields),
    };
  };

  useEffect(() => {
    if (!dialogOpen || dialogType !== 'product-category') return;
    setFormData((prev) => buildProductCategoryDraft(prev));
  }, [dialogOpen, dialogType, schemaCatalog]);

  const handleSave = async () => {
    try {
      switch (dialogType) {
        case 'product-category':
          const categoryPayload = buildProductCategoryDraft(formData);
          if (dialogMode === 'create') {
            await catalogsAPI.createProductCategory(categoryPayload);
            toast.success('Categoria criada com sucesso');
          } else {
            await catalogsAPI.updateProductCategory(selectedItem.id, categoryPayload);
            toast.success('Categoria actualizada');
          }
          break;
        case 'product-subcategory':
          if (dialogMode === 'create') {
            await catalogsAPI.createProductSubcategory(formData);
            toast.success('Subcategoria criada com sucesso');
          } else {
            await catalogsAPI.updateProductSubcategory(selectedItem.id, formData);
            toast.success('Subcategoria actualizada');
          }
          break;
        case 'service-catalog':
          if (dialogMode === 'create') {
            await catalogsAPI.createServiceCatalogItem({
              ...formData,
              unit_price: parseFloat(formData.unit_price) || 0
            });
            toast.success('Serviço criado com sucesso');
          } else {
            await catalogsAPI.updateServiceCatalogItem(selectedItem.id, {
              ...formData,
              unit_price: parseFloat(formData.unit_price) || 0
            });
            toast.success('Serviço actualizado');
          }
          break;
        case 'expense-category':
          if (dialogMode === 'create') {
            await catalogsAPI.createExpenseCategory(formData);
            toast.success('Categoria de despesa criada');
          } else {
            await catalogsAPI.updateExpenseCategory(selectedItem.id, formData);
            toast.success('Categoria actualizada');
          }
          break;
        case 'repair-service-type':
          if (dialogMode === 'create') {
            await catalogsAPI.createRepairServiceType({
              ...formData,
              default_price: formData.default_price ? parseFloat(formData.default_price) : null
            });
            toast.success('Tipo de reparação criado');
          } else {
            await catalogsAPI.updateRepairServiceType(selectedItem.id, {
              ...formData,
              default_price: formData.default_price ? parseFloat(formData.default_price) : null
            });
            toast.success('Tipo de reparação actualizado');
          }
          break;
        case 'cash-box-type':
          if (dialogMode === 'create') {
            await catalogsAPI.createCashBoxType({
              name: formData.name,
              location: formData.location,
              description: formData.description,
              is_main: formData.is_main || false
            });
            toast.success('Tipo de caixa criado com sucesso');
          } else {
            await catalogsAPI.updateCashBoxType(selectedItem.id, formData);
            toast.success('Tipo de caixa actualizado');
          }
          break;
        default:
          break;
      }
      setDialogOpen(false);
      loadAllCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDelete = async (type, id) => {
    // First check usage before showing delete dialog
    setDeleteLoading(true);
    setDeleteTarget({ type, id });
    
    try {
      let usageData = null;
      let itemData = null;
      
      if (type === 'product-category') {
        const response = await catalogsAPI.checkCategoryUsage(id);
        usageData = response.data;
        itemData = productCategories.find(c => c.id === id);
      } else if (type === 'product-subcategory') {
        const response = await catalogsAPI.checkSubcategoryUsage(id);
        usageData = response.data;
        itemData = productSubcategories.find(s => s.id === id);
      } else if (type === 'cash-box-type') {
        const response = await catalogsAPI.getCashBoxTypeUsage(id);
        usageData = response.data;
        itemData = cashBoxTypes.find(box => box.id === id);
      } else if (type === 'service-catalog') {
        // Service catalog doesn't have usage check yet, proceed directly
        itemData = serviceCatalog.find(s => s.id === id);
        usageData = { can_delete: true, total_usage: 0 };
      } else if (type === 'expense-category') {
        const response = await catalogsAPI.getExpenseCategoryUsage(id);
        usageData = response.data;
        itemData = expenseCategories.find(c => c.id === id) || expenseCategories.flatMap(c => c.subcategories || []).find(s => s.id === id);
      }
      
      setDeleteUsage(usageData);
      setDeleteTarget({ type, id, name: itemData?.name || usageData?.category_name || usageData?.subcategory_name });
      setDeleteDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleteLoading(false);
    }
  };

  const executeDelete = async (force = false) => {
    if (!deleteTarget) return;
    
    setDeleteLoading(true);
    try {
      switch (deleteTarget.type) {
        case 'product-category':
          await catalogsAPI.deleteProductCategory(deleteTarget.id, force);
          break;
        case 'product-subcategory':
          await catalogsAPI.deleteProductSubcategory(deleteTarget.id, force);
          break;
        case 'service-catalog':
          await catalogsAPI.deleteServiceCatalogItem(deleteTarget.id);
          break;
        case 'expense-category':
          await catalogsAPI.deleteExpenseCategory(deleteTarget.id, force);
          break;
        case 'cash-box-type':
          await catalogsAPI.deleteCashBoxType(deleteTarget.id, 'Eliminação manual via Catálogos');
          break;
        default:
          toast.error('Eliminação não suportada para este tipo');
          return;
      }
      toast.success(
        deleteTarget.type === 'expense-category'
          ? (force ? 'Categoria de despesa marcada como eliminada logicamente' : 'Categoria de despesa eliminada permanentemente')
          : (force ? 'Item marcado como eliminado logicamente' : 'Item eliminado permanentemente')
      );
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setDeleteUsage(null);
      loadAllCatalogs();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.usage) {
        // Backend returned usage info
        setDeleteUsage({
          ...errorDetail.usage,
          message: errorDetail.message,
          usage_summary: errorDetail.usage_summary,
          can_delete: false
        });
      } else {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteTarget) return;
    
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'product-category') {
        await catalogsAPI.updateProductCategory(deleteTarget.id, { is_active: false });
      } else if (deleteTarget.type === 'product-subcategory') {
        await catalogsAPI.updateProductSubcategory(deleteTarget.id, { is_active: false });
      } else if (deleteTarget.type === 'expense-category') {
        await catalogsAPI.updateExpenseCategory(deleteTarget.id, { is_active: false });
      }
      toast.success(
        deleteTarget.type === 'expense-category'
          ? 'Categoria de despesa inactivada. Não aparecerá em dropdowns, mas mantém o histórico.'
          : 'Item desactivado. Não aparecerá em dropdowns mas mantém histórico.'
      );
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setDeleteUsage(null);
      loadAllCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async (type, id) => {
    try {
      if (type === 'product-category') {
        await catalogsAPI.restoreProductCategory(id);
      } else if (type === 'product-subcategory') {
        await catalogsAPI.restoreProductSubcategory(id);
      } else if (type === 'expense-category') {
        await catalogsAPI.restoreExpenseCategory(id);
      }
      toast.success(type === 'expense-category' ? 'Categoria de despesa restaurada com sucesso!' : 'Item restaurado com sucesso!');
      loadAllCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleActivate = async (type, id) => {
    try {
      if (type === 'product-category') {
        await catalogsAPI.updateProductCategory(id, { is_active: true });
      } else if (type === 'product-subcategory') {
        await catalogsAPI.updateProductSubcategory(id, { is_active: true });
      } else if (type === 'expense-category') {
        await catalogsAPI.updateExpenseCategory(id, { is_active: true });
      }
      toast.success(type === 'expense-category' ? 'Categoria de despesa reactivada com sucesso!' : 'Item reactivado!');
      loadAllCatalogs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Filter function
  const filterItems = (items, searchFields = ['name']) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      searchFields.some(field => 
        item[field]?.toLowerCase().includes(query)
      )
    );
  };

  const selectedSubcategoryParent = productCategories.find((cat) => cat.id === formData.category_id);
  const dialogPosRulePrefix = dialogType === 'product-subcategory'
    ? (formData.prefix || selectedSubcategoryParent?.base_prefix || '')
    : (formData.base_prefix || '');

  // Render dialog content based on type
  const renderDialogContent = () => {
    switch (dialogType) {
      case 'product-category':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={MODAL_SECTION_CLASS}>
                <Label>Nome da Categoria *</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Laptop"
                />
              </div>
              <div className={MODAL_SECTION_CLASS}>
                <Label>Prefixo Base *</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  value={formData.base_prefix || ''}
                  onChange={(e) => setFormData({ ...formData, base_prefix: e.target.value.toUpperCase() })}
                  placeholder="Ex: LP"
                  maxLength={5}
                  disabled={dialogMode === 'edit'}
                />
                {dialogMode === 'edit' && (
                  <p className="text-xs text-muted-foreground">Prefixo não pode ser alterado</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <Switch
                checked={formData.is_trackable_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_trackable_default: checked })}
              />
              <Label>Produtos são rastreáveis por defeito (SN/IMEI)</Label>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Descrição</Label>
              <Textarea
                className={MODAL_TEXTAREA_CLASS}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
            <CatalogPosRuleEditor
              categoryPrefix={dialogPosRulePrefix}
              enabled={formData.pos_description_enabled}
              fields={formData.pos_description_fields}
              onChange={(updates) => setFormData({ ...formData, ...updates })}
              testIdPrefix="category"
            />
            <div className={MODAL_SECTION_CLASS} data-testid="category-schema-governance-section">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Governação do Schema</Label>
                  <p className="mt-1 text-xs text-slate-500">A categoria decide quais campos ficam activos, obrigatórios e quais atributos exigem nova variante.</p>
                </div>
                <Badge variant="outline">{(formData.schema_fields || []).filter((field) => field.active !== false).length} campos activos</Badge>
              </div>
              <div className="grid gap-3 md:grid-cols-2 mt-4">
                <div className="space-y-2">
                  <Label>Schema base</Label>
                  <Select
                    value={formData.schema_key || getSchemaBlueprintForCategory(formData.base_prefix, formData.schema_key)?.category_key || ''}
                    onValueChange={(value) => setFormData(buildProductCategoryDraft({ ...formData, schema_key: value }))}
                  >
                    <SelectTrigger className={MODAL_SELECT_CLASS} data-testid="category-schema-key-select">
                      <SelectValue placeholder="Seleccionar schema base" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(schemaCatalog)
                        .filter(([key, schema]) => schema?.governance?.source === 'hardcoded' && key === schema?.category_key)
                        .map(([key, schema]) => (
                          <SelectItem key={key} value={key}>{schema.display_name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Resumo</Label>
                  <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                    <p>POS por defeito: {(formData.pos_description_fields || []).length ? formData.pos_description_fields.join(', ') : 'catálogo legacy/fallback'}</p>
                    <p className="mt-1">Specs obrigatórias: {(formData.schema_fields || []).some((field) => field.active !== false && field.required) ? 'Sim' : 'Não'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <p className="text-sm font-medium text-slate-700">Atributos principais</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {normalizePrimaryAttributeRules(formData.primary_attribute_rules).map((rule) => (
                    <div key={rule.key} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`schema-primary-rule-${rule.key}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{rule.label}</p>
                          <p className="text-xs text-slate-500">{VARIANT_POLICY_OPTIONS.find((option) => option.value === rule.variant_policy)?.label || 'Só atributo'}</p>
                        </div>
                        <Switch
                          checked={rule.active !== false}
                          onCheckedChange={(checked) => setFormData(buildProductCategoryDraft({
                            ...formData,
                            primary_attribute_rules: normalizePrimaryAttributeRules(formData.primary_attribute_rules).map((item) => item.key === rule.key ? { ...item, active: checked } : item),
                          }))}
                          data-testid={`schema-primary-active-${rule.key}`}
                        />
                      </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-3">
                        <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                          <span className="text-xs text-slate-500">Obrigatório</span>
                          <Switch
                            checked={Boolean(rule.required)}
                            onCheckedChange={(checked) => setFormData(buildProductCategoryDraft({
                              ...formData,
                              primary_attribute_rules: normalizePrimaryAttributeRules(formData.primary_attribute_rules).map((item) => item.key === rule.key ? { ...item, required: checked } : item),
                            }))}
                            data-testid={`schema-primary-required-${rule.key}`}
                          />
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                          <span className="text-xs text-slate-500">Destaque</span>
                          <Switch
                            checked={Boolean(rule.highlight)}
                            onCheckedChange={(checked) => setFormData(buildProductCategoryDraft({
                              ...formData,
                              primary_attribute_rules: normalizePrimaryAttributeRules(formData.primary_attribute_rules).map((item) => item.key === rule.key ? { ...item, highlight: checked } : item),
                            }))}
                            data-testid={`schema-primary-highlight-${rule.key}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Política</Label>
                          <Select
                            value={rule.variant_policy || 'attribute_only'}
                            onValueChange={(value) => setFormData(buildProductCategoryDraft({
                              ...formData,
                              primary_attribute_rules: normalizePrimaryAttributeRules(formData.primary_attribute_rules).map((item) => item.key === rule.key ? { ...item, variant_policy: value } : item),
                            }))}
                          >
                            <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-slate-50" data-testid={`schema-primary-policy-${rule.key}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {VARIANT_POLICY_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <p className="text-sm font-medium text-slate-700">Campos dinâmicos governados</p>
                <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
                  {Object.entries((formData.schema_fields || []).reduce((acc, field) => {
                    const group = field.group_label || 'Geral';
                    if (!acc[group]) acc[group] = [];
                    acc[group].push(field);
                    return acc;
                  }, {})).map(([groupLabel, fields]) => (
                    <div key={groupLabel} className="rounded-xl border border-slate-200 bg-white p-3">
                      <p className="mb-3 text-sm font-medium text-slate-900">{groupLabel}</p>
                      <div className="space-y-2">
                        {fields.map((field) => (
                          <div key={field.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2" data-testid={`schema-field-row-${field.id}`}>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{field.label}</p>
                              <p className="text-xs text-slate-500">{field.key}</p>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <label className="flex items-center gap-2">
                                <span>Activo</span>
                                <Switch
                                  checked={field.active !== false}
                                  onCheckedChange={(checked) => setFormData(buildProductCategoryDraft({
                                    ...formData,
                                    schema_fields: (formData.schema_fields || []).map((item) => item.id === field.id ? { ...item, active: checked } : item),
                                  }))}
                                  data-testid={`schema-field-active-${field.id}`}
                                />
                              </label>
                              <label className="flex items-center gap-2">
                                <span>Obrig.</span>
                                <Switch
                                  checked={Boolean(field.required)}
                                  onCheckedChange={(checked) => setFormData(buildProductCategoryDraft({
                                    ...formData,
                                    schema_fields: (formData.schema_fields || []).map((item) => item.id === field.id ? { ...item, required: checked } : item),
                                  }))}
                                  data-testid={`schema-field-required-${field.id}`}
                                />
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'product-subcategory':
        return (
          <div className="space-y-5">
            <div className={MODAL_SECTION_CLASS}>
              <Label>Categoria Pai *</Label>
              <Select
                value={formData.category_id || ''}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                disabled={dialogMode === 'edit'}
              >
                <SelectTrigger className={MODAL_SELECT_CLASS}>
                  <SelectValue placeholder="Selecionar categoria" />
                </SelectTrigger>
                <SelectContent>
                  {productCategories.filter(c => c.is_active).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.base_prefix} - {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={MODAL_SECTION_CLASS}>
                <Label>Nome da Subcategoria *</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Gaming"
                />
              </div>
              <div className={MODAL_SECTION_CLASS}>
                <Label>Prefixo Único *</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  value={formData.prefix || ''}
                  onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                  placeholder="Ex: LPG"
                  maxLength={6}
                  disabled={dialogMode === 'edit'}
                />
              </div>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Rastreável (sobrescrever categoria)</Label>
              <Select
                value={formData.is_trackable_override === null || formData.is_trackable_override === undefined ? 'inherit' : formData.is_trackable_override.toString()}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  is_trackable_override: value === 'inherit' ? null : value === 'true'
                })}
              >
                <SelectTrigger className={MODAL_SELECT_CLASS}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Herdar da categoria</SelectItem>
                  <SelectItem value="true">Sim - Rastreável</SelectItem>
                  <SelectItem value="false">Não - Quantidade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <CatalogPosRuleEditor
              categoryPrefix={dialogPosRulePrefix}
              enabled={formData.pos_description_enabled}
              fields={formData.pos_description_fields}
              onChange={(updates) => setFormData({ ...formData, ...updates })}
              testIdPrefix="subcategory"
            />
          </div>
        );

      case 'service-catalog':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={MODAL_SECTION_CLASS}>
                <Label>Grupo *</Label>
                <Select
                  value={formData.service_group || 'SP'}
                  onValueChange={(value) => setFormData({ ...formData, service_group: value })}
                >
                  <SelectTrigger className={MODAL_SELECT_CLASS}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SP">SP - Serviços Gerais</SelectItem>
                    <SelectItem value="GP">GP - Gaming Center</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={MODAL_SECTION_CLASS}>
                <Label>Nome do Serviço *</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Cópia PB"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={MODAL_SECTION_CLASS}>
                <Label>Preço Unitário (MT) *</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  type="number"
                  value={formData.unit_price || ''}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className={MODAL_SECTION_CLASS}>
                <Label>Unidade *</Label>
                <Select
                  value={formData.unit || 'unidade'}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger className={MODAL_SELECT_CLASS}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unidade">Por unidade</SelectItem>
                    <SelectItem value="página">Por página</SelectItem>
                    <SelectItem value="10 minutos">Por 10 minutos</SelectItem>
                    <SelectItem value="serviço">Por serviço</SelectItem>
                    <SelectItem value="hora">Por hora</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Descrição</Label>
              <Textarea
                className={MODAL_TEXTAREA_CLASS}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
        );

      case 'expense-category':
        return (
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4" data-testid="expense-category-code-pattern-card">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Padrão oficial</p>
              <p className="mt-2 text-sm text-slate-700">Categoria: <span className="font-mono font-semibold">PGT00</span> · Subcategoria: <span className="font-mono font-semibold">PGT00.00</span></p>
              <p className="mt-2 text-xs text-muted-foreground">O código é gerado automaticamente e respeita a sequência oficial sem reutilizar códigos ocupados.</p>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Categoria Pai (opcional)</Label>
              <Select
                value={formData.parent_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value === 'none' ? null : value })}
              >
                <SelectTrigger className={MODAL_SELECT_CLASS} data-testid="expense-category-parent-select">
                  <SelectValue placeholder="Nenhuma (categoria principal)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma (categoria principal)</SelectItem>
                  {expenseCategories.filter(c => !c.parent_id && !c.is_deleted).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.expense_code} - {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Nome *</Label>
              <Input
                className={MODAL_INPUT_CLASS}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Material de Limpeza"
                data-testid="expense-category-name-input"
              />
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Descrição</Label>
              <Textarea
                className={MODAL_TEXTAREA_CLASS}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="expense-category-description-input"
              />
            </div>
          </div>
        );

      case 'repair-service-type':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={MODAL_SECTION_CLASS}>
                <Label>Categoria *</Label>
                <Select
                  value={formData.category || 'laptop'}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className={MODAL_SELECT_CLASS}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laptop">Laptop</SelectItem>
                    <SelectItem value="smartphone">Smartphone</SelectItem>
                    <SelectItem value="desktop">Desktop</SelectItem>
                    <SelectItem value="console">Consola</SelectItem>
                    <SelectItem value="audio">Áudio</SelectItem>
                    <SelectItem value="diagnostico">Diagnóstico</SelectItem>
                    <SelectItem value="outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={MODAL_SECTION_CLASS}>
                <Label>Preço Default (MT)</Label>
                <Input
                  className={MODAL_INPUT_CLASS}
                  type="number"
                  value={formData.default_price || ''}
                  onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                  placeholder="Opcional"
                />
              </div>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Nome do Tipo de Reparação *</Label>
              <Input
                className={MODAL_INPUT_CLASS}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Troca de display"
              />
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Descrição</Label>
              <Textarea
                className={MODAL_TEXTAREA_CLASS}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
        );

      case 'cash-box-type':
        return (
          <div className="space-y-5">
            <div className={MODAL_SECTION_CLASS}>
              <Label>Nome do Tipo de Caixa *</Label>
              <Input
                className={MODAL_INPUT_CLASS}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Caixa Principal Loja"
              />
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Local / Departamento</Label>
              <Input
                className={MODAL_INPUT_CLASS}
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Loja Maputo Centro"
              />
            </div>
            <div className="flex items-center space-x-2 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <Switch
                checked={formData.is_main || false}
                onCheckedChange={(checked) => setFormData({ ...formData, is_main: checked })}
              />
              <Label>Caixa Principal (apenas pode haver uma)</Label>
            </div>
            <div className={MODAL_SECTION_CLASS}>
              <Label>Descrição</Label>
              <Textarea
                className={MODAL_TEXTAREA_CLASS}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    const action = dialogMode === 'create' ? 'Criar' : 'Editar';
    switch (dialogType) {
      case 'product-category': return `${action} Categoria de Produto`;
      case 'product-subcategory': return `${action} Subcategoria`;
      case 'service-catalog': return `${action} Serviço`;
      case 'expense-category': return `${action} Categoria de Despesa`;
      case 'repair-service-type': return `${action} Tipo de Reparação`;
      case 'cash-operation-type': return `${action} Operação de Caixa`;
      case 'cash-box-type': return `${action} Tipo de Caixa`;
      default: return action;
    }
  };

  const hasCatalogData = [
    productCategories,
    productSubcategories,
    serviceCatalog,
    expenseCategories,
    repairServiceTypes,
    cashOperationTypes,
    cashBoxTypes,
  ].some((items) => Array.isArray(items) && items.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">A carregar catálogos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalogs-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Catálogos</h1>
          <p className="text-muted-foreground">Gestão de categorias, serviços, despesas e mais</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Switch 
              checked={showDeleted} 
              onCheckedChange={setShowDeleted}
              id="show-deleted"
            />
            <Label htmlFor="show-deleted" className="text-sm text-muted-foreground cursor-pointer">
              Mostrar eliminados
            </Label>
          </div>
        )}
      </div>

      {!hasCatalogData && (
        <EmptyStatePanel
          title="Sem dados disponíveis"
          description="Nenhum registo encontrado nos catálogos desta base. Pode configurar ou importar dados para começar."
          testId="catalogs-empty-state"
        />
      )}

      {partialIssues.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-900" data-testid="catalogs-partial-issues">
          <AlertTriangle className="mt-0.5 h-5 w-5" />
          <div>
            <p className="font-medium">Falhas reais detectadas no carregamento dos catálogos</p>
            <p className="text-sm opacity-80">Os endpoints abaixo falharam e já não estão a ser escondidos por fallback silencioso.</p>
            <ul className="mt-3 space-y-1 text-sm" data-testid="catalogs-partial-issues-list">
              {partialIssues.slice(0, 5).map((issue, index) => (
                <li key={`${issue.endpoint}-${index}`} data-testid={`catalogs-partial-issues-item-${index}`}>
                  • {issue.endpoint}{issue.httpStatus ? ` — HTTP ${issue.httpStatus}` : ''}{issue.errorMessage ? ` — ${issue.errorMessage}` : ''}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Search + Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="grid gap-3 xl:grid-cols-[minmax(320px,400px)_minmax(0,1fr)] xl:items-start">
          <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Controlos</p>
                <p className="text-sm text-slate-500">Pesquisa rápida nos catálogos.</p>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar em todos os catálogos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 rounded-xl border-slate-300 bg-slate-50 pl-10 pr-10"
                data-testid="catalogs-search-input"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full p-0 text-slate-500 hover:bg-slate-200"
                  onClick={() => setSearchQuery('')}
                  data-testid="catalogs-search-clear-button"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <TabsList className="flex h-auto w-full flex-wrap items-center justify-start gap-2 rounded-[24px] border border-slate-200 bg-white p-2 shadow-sm xl:min-h-[104px]">
          <TabsTrigger value="product-categories" className="flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            <span className="hidden sm:inline">Categorias</span>
          </TabsTrigger>
          <TabsTrigger value="subcategories" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Subcategorias</span>
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2">
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Serviços</span>
          </TabsTrigger>
          <TabsTrigger value="expenses" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            <span className="hidden sm:inline">Despesas</span>
          </TabsTrigger>
          <TabsTrigger value="repairs" className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            <span className="hidden sm:inline">Reparações</span>
          </TabsTrigger>
          <TabsTrigger value="cash-ops" className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            <span className="hidden sm:inline">Caixa</span>
          </TabsTrigger>
          <TabsTrigger value="cash-box-types" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Tipos de Caixa</span>
          </TabsTrigger>
          </TabsList>
        </div>

        {/* Product Categories Tab */}
        <TabsContent value="product-categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">
              Categorias de Produtos ({productCategories.filter(c => !c.is_deleted).length})
              {showDeleted && productCategories.filter(c => c.is_deleted).length > 0 && (
                <span className="text-sm text-red-500 ml-2">
                  (+{productCategories.filter(c => c.is_deleted).length} eliminados)
                </span>
              )}
            </h2>
            {isAdmin && (
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={() => openCreateDialog('product-category')} data-testid="new-category-btn">
                <Plus className="h-4 w-4 mr-2" /> Nova Categoria
              </Button>
            )}
          </div>
          <div className="grid items-start gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            {filterItems(productCategories).map(cat => (
              <Card 
                key={cat.id} 
                className={cn(
                  "transition-all",
                  cat.is_deleted && "border-red-300 bg-red-50 opacity-70",
                  !cat.is_active && !cat.is_deleted && "opacity-60 border-amber-300 bg-amber-50"
                )}
                data-testid={`category-card-${cat.base_prefix}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge variant="outline" className="mb-2 font-mono text-[11px]">{cat.base_prefix}</Badge>
                      <CardTitle className="min-h-[44px] text-base leading-tight">{cat.name}</CardTitle>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Badge variant="secondary" className="text-[10px]">
                          {(cat.effective_schema?.schema_fields || []).filter((field) => field.active !== false).length} campos
                        </Badge>
                        <Badge variant={(cat.effective_schema?.specs_required || cat.specs_required) ? 'default' : 'outline'} className="text-[10px]">
                          {(cat.effective_schema?.specs_required || cat.specs_required) ? 'Specs obrigatórias' : 'Specs opcionais'}
                        </Badge>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        {cat.is_deleted ? (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleRestore('product-category', cat.id)}
                            title="Restaurar"
                          >
                            <RotateCcw className="h-4 w-4 text-green-600" />
                          </Button>
                        ) : (
                          <>
                            {!cat.is_active && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleActivate('product-category', cat.id)}
                                title="Reactivar"
                              >
                                <Eye className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog('product-category', cat)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDelete('product-category', cat.id)}
                              data-testid={`delete-category-${cat.base_prefix}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge variant={cat.is_trackable_default ? 'default' : 'secondary'} className="text-[11px]">
                      {cat.is_trackable_default ? 'Rastreável' : 'Quantidade'}
                    </Badge>
                    <Badge variant={cat.pos_description_enabled ? 'default' : 'outline'} className="text-[11px]" data-testid={`category-pos-rule-${cat.id}`}>
                      {cat.pos_description_enabled ? 'POS ON' : 'POS OFF'}
                    </Badge>
                    {cat.is_deleted && <Badge variant="destructive" className="text-[11px]">Eliminado</Badge>}
                    {!cat.is_active && !cat.is_deleted && <Badge variant="outline" className="border-amber-500 text-[11px] text-amber-700">Inactivo</Badge>}
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Subcategorias activas</p>
                    <p className="mt-1 text-sm font-medium text-slate-700">
                      {productSubcategories.filter(s => s.category_id === cat.id && !s.is_deleted).length} subcategorias
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Subcategories Tab */}
        <TabsContent value="subcategories" className="space-y-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <h2 className="text-xl font-semibold">
                Subcategorias ({productSubcategories.filter(s => !s.is_deleted).length})
              </h2>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-11 w-56 rounded-xl border-slate-300 bg-slate-50 shadow-sm">
                  <SelectValue placeholder="Filtrar por categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {productCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={() => openCreateDialog('product-subcategory')}>
                <Plus className="h-4 w-4 mr-2" /> Nova Subcategoria
              </Button>
            )}
          </div>
          <div className="grid items-start gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            {filterItems(
              productSubcategories.filter(s => selectedCategory === 'all' || s.category_id === selectedCategory),
              ['name', 'prefix']
            ).map(sub => (
              <Card 
                key={sub.id} 
                className={cn(
                  "transition-all",
                  sub.is_deleted && "border-red-300 bg-red-50 opacity-70",
                  !sub.is_active && !sub.is_deleted && "opacity-60 border-amber-200 bg-amber-50"
                )}
                data-testid={`subcategory-card-${sub.prefix}`}
              >
                <CardContent className="space-y-3 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge variant="outline" className="mb-2 font-mono text-[11px]">{sub.prefix}</Badge>
                      <p className="min-h-[40px] font-medium leading-tight text-slate-900">{sub.name}</p>
                      <p className="text-xs text-muted-foreground">{sub.category_name}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        {sub.is_deleted ? (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleRestore('product-subcategory', sub.id)}
                            title="Restaurar"
                          >
                            <RotateCcw className="h-4 w-4 text-green-600" />
                          </Button>
                        ) : (
                          <>
                            {!sub.is_active && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleActivate('product-subcategory', sub.id)}
                                title="Reactivar"
                              >
                                <Eye className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog('product-subcategory', sub)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDelete('product-subcategory', sub.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={sub.is_trackable ? 'default' : 'secondary'} className="text-xs">
                      {sub.is_trackable ? 'SN/IMEI' : 'QTD'}
                    </Badge>
                    <Badge variant={sub.pos_description_enabled ? 'default' : 'outline'} className="text-xs" data-testid={`subcategory-pos-rule-${sub.id}`}>
                      {sub.pos_description_enabled ? 'POS ON' : 'POS OFF'}
                    </Badge>
                    {sub.is_deleted && <Badge variant="destructive" className="text-xs">Eliminado</Badge>}
                    {!sub.is_active && !sub.is_deleted && <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">Inactivo</Badge>}
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Sequência actual</p>
                    <p className="mt-1 text-sm font-medium text-slate-700">{sub.current_sequence}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Service Catalog Tab */}
        <TabsContent value="services" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Catálogo de Serviços ({serviceCatalog.length})</h2>
            {isAdmin && (
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={() => openCreateDialog('service-catalog')}>
                <Plus className="h-4 w-4 mr-2" /> Novo Serviço
              </Button>
            )}
          </div>
          
          {/* Group by service_group */}
          {['SP', 'GP'].map(group => {
            const groupServices = filterItems(
              serviceCatalog.filter(s => s.service_group === group),
              ['name', 'service_code']
            );
            if (groupServices.length === 0) return null;
            
            return (
              <Card key={group} className="rounded-[24px] border-slate-200 bg-white shadow-sm">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="flex items-center gap-2">
                    {group === 'SP' ? <Printer className="h-5 w-5" /> : <Gamepad2 className="h-5 w-5" />}
                    {group === 'SP' ? 'Serviços Gerais (SP)' : 'Gaming Center (GP)'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid gap-2">
                    {groupServices.map(svc => (
                      <div 
                        key={svc.id} 
                        className={`flex justify-between items-center p-3 rounded-lg border ${!svc.is_active ? 'opacity-60 bg-slate-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono">{svc.service_code}</Badge>
                          <span>{svc.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-green-600">
                            {formatCurrency(svc.unit_price)}
                          </span>
                          <span className="text-sm text-muted-foreground">/{svc.unit}</span>
                          {isAdmin && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog('service-catalog', svc)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete('service-catalog', svc.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Expense Categories Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex justify-between items-center gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-semibold" data-testid="expense-categories-section-title">{t('catalogsPage.expenseCategoriesTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1" data-testid="expense-categories-section-description">Cadastro normalizado com código oficial PGT, subcategoria dependente e eliminação segura.</p>
            </div>
            {isAdmin && (
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={() => openCreateDialog('expense-category')} data-testid="expense-categories-new-button">
                <Plus className="h-4 w-4 mr-2" /> {t('catalogsPage.newCategory')}
              </Button>
            )}
          </div>
          <div className="grid gap-3">
            {filterItems(expenseCategories, ['name', 'expense_code']).map(cat => (
              <Card key={cat.id} className={cn('rounded-[24px] border-slate-200 bg-white shadow-sm', cat.is_deleted && 'border-red-200 bg-red-50/50', !cat.is_deleted && !cat.is_active && 'border-amber-200 bg-amber-50/50')} data-testid={`expense-category-card-${cat.id}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Badge variant="outline" className="font-mono" data-testid={`expense-category-code-${cat.id}`}>{cat.expense_code}</Badge>
                        <span className="font-medium" data-testid={`expense-category-name-${cat.id}`}>{cat.name}</span>
                        {cat.is_deleted ? <Badge variant="destructive" data-testid={`expense-category-status-${cat.id}`}>Eliminada</Badge> : !cat.is_active ? <Badge variant="outline" className="border-amber-200 bg-amber-100 text-amber-700" data-testid={`expense-category-status-${cat.id}`}>Inactiva</Badge> : <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-emerald-700" data-testid={`expense-category-status-${cat.id}`}>Activa</Badge>}
                      </div>
                      {cat.description && <p className="max-w-3xl text-sm text-muted-foreground" data-testid={`expense-category-description-${cat.id}`}>{cat.description}</p>}
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        {cat.is_deleted ? (
                          <Button variant="ghost" size="icon" onClick={() => handleRestore('expense-category', cat.id)} data-testid={`expense-category-restore-${cat.id}`}>
                            <RotateCcw className="h-4 w-4 text-emerald-600" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog('expense-category', cat)} data-testid={`expense-category-edit-${cat.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {!cat.is_active && (
                              <Button variant="ghost" size="icon" onClick={() => handleActivate('expense-category', cat.id)} data-testid={`expense-category-activate-${cat.id}`}>
                                <Eye className="h-4 w-4 text-emerald-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleDelete('expense-category', cat.id)} data-testid={`expense-category-delete-${cat.id}`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {cat.subcategories && cat.subcategories.length > 0 && (
                    <div className="mt-3 pl-4 border-l-2 border-slate-200 space-y-1">
                      {cat.subcategories.map(sub => (
                        <div key={sub.id} className={cn('flex items-center justify-between gap-2 rounded-xl px-2 py-2 text-sm', sub.is_deleted && 'bg-red-50', !sub.is_deleted && !sub.is_active && 'bg-amber-50')} data-testid={`expense-subcategory-row-${sub.id}`}>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                              <Badge variant="secondary" className="font-mono text-xs" data-testid={`expense-subcategory-code-${sub.id}`}>{sub.expense_code}</Badge>
                              <span data-testid={`expense-subcategory-name-${sub.id}`}>{sub.name}</span>
                              {sub.is_deleted ? <Badge variant="destructive" className="text-[10px]">Eliminada</Badge> : !sub.is_active ? <Badge variant="outline" className="border-amber-200 bg-amber-100 text-amber-700 text-[10px]">Inactiva</Badge> : null}
                            </div>
                            {sub.description && <p className="pl-5 text-xs text-muted-foreground">{sub.description}</p>}
                          </div>
                          {isAdmin && (
                            <div className="flex items-center gap-1">
                              {sub.is_deleted ? (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRestore('expense-category', sub.id)} data-testid={`expense-subcategory-restore-${sub.id}`}>
                                  <RotateCcw className="h-3.5 w-3.5 text-emerald-600" />
                                </Button>
                              ) : (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog('expense-category', sub)} data-testid={`expense-subcategory-edit-${sub.id}`}>
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  {!sub.is_active && (
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleActivate('expense-category', sub.id)} data-testid={`expense-subcategory-activate-${sub.id}`}>
                                      <Eye className="h-3.5 w-3.5 text-emerald-600" />
                                    </Button>
                                  )}
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete('expense-category', sub.id)} data-testid={`expense-subcategory-delete-${sub.id}`}>
                                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Repair Service Types Tab */}
        <TabsContent value="repairs" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t('catalogsPage.repairTypesTitle')}</h2>
            {isAdmin && (
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={() => openCreateDialog('repair-service-type')}>
                <Plus className="h-4 w-4 mr-2" /> {t('catalogsPage.newType')}
              </Button>
            )}
          </div>
          
          {/* Group by category */}
          {['laptop', 'smartphone', 'console', 'desktop', 'diagnostico', 'audio', 'outros'].map(category => {
            const categoryTypes = filterItems(
              repairServiceTypes.filter(t => t.category === category),
              ['name', 'code']
            );
            if (categoryTypes.length === 0) return null;
            
            const categoryLabels = {
              laptop: 'Laptop',
              smartphone: 'Smartphone',
              console: 'Consola',
              desktop: 'Desktop',
              diagnostico: 'Diagnóstico',
              audio: 'Áudio',
              outros: 'Outros'
            };
            
            return (
              <Card key={category} className="rounded-[24px] border-slate-200 bg-white shadow-sm">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg">{categoryLabels[category]}</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid gap-2 md:grid-cols-2">
                    {categoryTypes.map(type => (
                      <div 
                        key={type.id} 
                        className={`flex justify-between items-center p-3 rounded-lg border ${!type.is_active ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono">{type.code}</Badge>
                          <span>{type.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {type.default_price && (
                            <span className="text-sm text-green-600 font-medium">
                              {formatCurrency(type.default_price)}
                            </span>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog('repair-service-type', type)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Cash Operation Types Tab */}
        <TabsContent value="cash-ops" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t('catalogsPage.cashOpsTitle')}</h2>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={handleSeedCashOperationTypes}
                disabled={seedingCashOps}
                data-testid="catalogs-seed-cash-operation-types-button"
              >
                {seedingCashOps ? <RotateCcw className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                {t('catalogsPage.resetCashOps')}
              </Button>
            )}
          </div>

          {cashOperationTypes.length === 0 && (
            <Card className="rounded-[24px] border-amber-200 bg-amber-50 shadow-sm" data-testid="catalogs-cash-ops-empty-state">
              <CardContent className="py-8 text-center text-amber-900">
                <Banknote className="mx-auto mb-4 h-10 w-10 opacity-80" />
                <p className="font-medium">{t('catalogsPage.noCashOps')}</p>
                <p className="mt-2 text-sm text-amber-800">{t('catalogsPage.noCashOpsHelp')}</p>
              </CardContent>
            </Card>
          )}
          
          {/* Group by direction */}
          {[
            { key: 'out', label: 'Levantamentos', icon: Banknote },
            { key: 'transfer', label: 'Transferências', icon: ArrowLeftRight },
            { key: 'in', label: 'Entradas', icon: CreditCard }
          ].map(({ key, label, icon: Icon }) => {
            const ops = filterItems(
              cashOperationTypes.filter(o => o.direction === key),
              ['name', 'code']
            );
            if (ops.length === 0) return null;
            
            return (
              <Card key={key} className="rounded-[24px] border-slate-200 bg-white shadow-sm">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Icon className="h-5 w-5" />
                    {label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid gap-2 md:grid-cols-2">
                    {ops.map(op => (
                      <div key={op.id} className="flex items-center gap-2 p-2 rounded border">
                        <Badge variant="outline" className="font-mono">{op.code}</Badge>
                        <span className="text-sm">{op.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Cash Box Types Tab */}
        <TabsContent value="cash-box-types" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Tipos de Caixa</h2>
            {isAdmin && (
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={() => openCreateDialog('cash-box-type')}>
                <Plus className="h-4 w-4 mr-2" /> Novo Tipo de Caixa
              </Button>
            )}
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(Array.isArray(cashBoxTypes) ? cashBoxTypes : []).length === 0 ? (
              <Card className="col-span-full rounded-[24px] border-slate-200 bg-white shadow-sm">
                <CardContent className="py-8 text-center text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum tipo de caixa definido.</p>
                  {isAdmin && <p className="text-sm mt-2">Clique em &quot;Novo Tipo de Caixa&quot; para criar.</p>}
                </CardContent>
              </Card>
            ) : (
              (Array.isArray(cashBoxTypes) ? cashBoxTypes : []).map((boxType) => (
                <Card key={boxType.id} className="rounded-[24px] border-slate-200 bg-white shadow-sm">
                  <CardHeader className="border-b border-slate-100 pb-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5" />
                          {boxType.name}
                        </CardTitle>
                        <CardDescription className="font-mono text-sm mt-1">
                          {boxType.code}
                        </CardDescription>
                      </div>
                      <div className="flex gap-1">
                        {boxType.is_main && (
                          <Badge className="bg-green-100 text-green-800">Principal</Badge>
                        )}
                        <Badge className={boxType.is_active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                          {boxType.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {boxType.location && (
                      <p className="text-sm text-muted-foreground mb-2">
                        <strong>Local:</strong> {boxType.location}
                      </p>
                    )}
                    {boxType.description && (
                      <p className="text-sm text-muted-foreground">{boxType.description}</p>
                    )}
                    {isAdmin && (
                      <div className="flex gap-2 mt-4 pt-3 border-t">
                        <Button size="sm" variant="outline" className="rounded-xl" onClick={() => openEditDialog('cash-box-type', boxType)}>
                          <Edit className="h-4 w-4 mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-xl text-red-600" onClick={() => handleDelete('cash-box-type', boxType.id)}>
                          <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-[28px] border-slate-200 bg-white shadow-xl">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>
              Preencha os campos abaixo para manter os catálogos coerentes e operacionais.
            </DialogDescription>
          </DialogHeader>
          {renderDialogContent()}
          <DialogFooter className="border-t border-slate-100 pt-4">
            <Button className="h-11 rounded-xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={handleSave}>
              {dialogMode === 'create' ? 'Criar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog with Usage Info */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
          setDeleteUsage(null);
        }
      }}>
        <DialogContent className="max-w-md rounded-[28px] border-slate-200 bg-white shadow-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {deleteUsage?.can_delete ? (
                <Trash2 className="h-5 w-5 text-red-500" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-amber-500" />
              )}
              {deleteUsage?.can_delete ? 'Confirmar Eliminação' : 'Item em Uso'}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.name && (
                <span className="font-semibold text-foreground">{deleteTarget.name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {deleteUsage && !deleteUsage.can_delete && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Este item está em uso no sistema</p>
                      <p className="text-sm text-amber-700 mt-1">
                        {deleteUsage.usage_summary || `${deleteUsage.total_usage} referências encontradas`}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Usage Details */}
                <div className="text-sm space-y-2 border rounded-lg p-3 bg-slate-50">
                  <p className="font-medium text-slate-700">Detalhes de utilização:</p>
                  <ul className="space-y-1 text-slate-600">
                    {deleteUsage.products > 0 && (
                      <li className="flex justify-between">
                        <span>Produtos</span>
                        <Badge variant="secondary">{deleteUsage.products}</Badge>
                      </li>
                    )}
                    {deleteUsage.items > 0 && (
                      <li className="flex justify-between">
                        <span>Items em Stock</span>
                        <Badge variant="secondary">{deleteUsage.items}</Badge>
                      </li>
                    )}
                    {deleteUsage.expenses > 0 && (
                      <li className="flex justify-between">
                        <span>Despesas</span>
                        <Badge variant="secondary">{deleteUsage.expenses}</Badge>
                      </li>
                    )}
                    {deleteUsage.services > 0 && (
                      <li className="flex justify-between">
                        <span>Serviços</span>
                        <Badge variant="secondary">{deleteUsage.services}</Badge>
                      </li>
                    )}
                    {deleteUsage.sales_items > 0 && (
                      <li className="flex justify-between">
                        <span>Vendas</span>
                        <Badge variant="secondary">{deleteUsage.sales_items}</Badge>
                      </li>
                    )}
                    {deleteUsage.kits > 0 && (
                      <li className="flex justify-between">
                        <span>Kits/Bundles</span>
                        <Badge variant="secondary">{deleteUsage.kits}</Badge>
                      </li>
                    )}
                    {deleteUsage.subcategories > 0 && (
                      <li className="flex justify-between">
                        <span>Subcategorias</span>
                        <Badge variant="secondary">{deleteUsage.subcategories}</Badge>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">O que pretende fazer?</p>
                  <div className="grid gap-2">
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto rounded-2xl py-3"
                      onClick={handleDeactivate}
                      disabled={deleteLoading}
                    >
                      <EyeOff className="h-4 w-4 mr-3 text-amber-600" />
                      <div className="text-left">
                        <div className="font-medium">Desactivar</div>
                        <div className="text-xs text-muted-foreground">Não aparece em dropdowns, mantém histórico</div>
                      </div>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto rounded-2xl border-red-200 py-3 hover:bg-red-50"
                      onClick={() => executeDelete(true)}
                      disabled={deleteLoading}
                    >
                      <Trash2 className="h-4 w-4 mr-3 text-red-600" />
                      <div className="text-left">
                        <div className="font-medium text-red-700">Eliminar (soft delete)</div>
                        <div className="text-xs text-muted-foreground">Marca como eliminado, pode restaurar depois</div>
                      </div>
                    </Button>
                  </div>
                </div>
              </>
            )}

            {deleteUsage?.can_delete && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Este item não está em uso em nenhum módulo e pode ser eliminado permanentemente.
                </p>
                <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
                  <Button className="h-11 rounded-xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="h-11 rounded-xl"
                    onClick={() => executeDelete(false)}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'A eliminar...' : 'Eliminar Permanentemente'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {!deleteUsage?.can_delete && (
            <DialogFooter className="border-t border-slate-100 pt-4">
              <Button className="h-11 rounded-xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
